import { DueDiligencePageGetDueDiligenceRecordQuery } from '@/__generated__/graphql';
import { ITemplateStylingSettings } from '@/components/settings-page/platformSettings/templatesSettings/types';
import { IFieldEditor } from '@/shared/types/fieldEditor';
import * as docx from 'docx';
import { DefaultTheme } from 'styled-components/dist/types';
import { convertDueDiligenceFieldToFieldEditor } from '../../fieldList';
import { getOneColumnSection } from './components/sections/oneColumnSection';
import { getThreeColumnSection } from './components/sections/threeColumnSection';
import { getTwoColumnSection } from './components/sections/twoColumnSection';
import { createInvestmentParagraphsForDOCX, getScorePropertiesForDOCX, transparentDocxTableBorders } from './utils';

const shouldDisplaySection = (fields: IFieldEditor[], includeAiSuggested: boolean) => {
  return fields.some((field) => {
    const hasValueDifferentFromAI = field.value && field.value !== field.AI?.answers[0];
    const hasAiSuggestedIncluded = field.AI?.answers.length && includeAiSuggested && (field.value === null || field.value === field.AI?.answers[0]);
    const hasIncludedAttachments = field.attachments.length;

    return hasValueDifferentFromAI || hasAiSuggestedIncluded || hasIncludedAttachments;
  });
};

const createSectionHeader = (
  section: DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['funnel']['tiers'][number]['sections'][number],
  headerFontColor: string,
  theme: DefaultTheme
) => {
  const sectionHeaderTable = new docx.Table({
    rows: [
      new docx.TableRow({
        children: [
          new docx.TableCell({
            children: [
              new docx.Paragraph({
                children: [
                  new docx.TextRun({
                    text: section.name,
                    color: headerFontColor
                  })
                ],
                spacing: { after: 100 },
                heading: docx.HeadingLevel.HEADING_1
              })
            ],
            borders: {
              ...transparentDocxTableBorders,
              bottom: { style: docx.BorderStyle.SINGLE, size: 6, color: theme.layer[2] }
            },
            width: {
              size: 8600,
              type: docx.WidthType.DXA
            }
          }),
          new docx.TableCell({
            children: [
              new docx.Paragraph({
                children: [
                  new docx.TextRun({
                    text: section.userCanScore ? 'Score\t' : '',
                    size: 24,
                    color: headerFontColor
                  })
                ]
              })
            ],
            borders: {
              ...transparentDocxTableBorders,
              bottom: { style: docx.BorderStyle.SINGLE, size: 6, color: theme.layer[2] }
            },
            width: {
              size: 750,
              type: docx.WidthType.DXA
            },
            verticalAlign: docx.VerticalAlign.CENTER
          }),
          new docx.TableCell({
            children: [
              new docx.Paragraph({
                children: [
                  new docx.TextRun({
                    text: section.userCanScore ? getScorePropertiesForDOCX(section.data.score, theme).text : '',
                    size: 24,
                    color: getScorePropertiesForDOCX(section.data.score, theme).color,
                    bold: true
                  })
                ],
                alignment: docx.AlignmentType.CENTER
              })
            ],
            borders: {
              ...transparentDocxTableBorders,
              bottom: { style: docx.BorderStyle.SINGLE, size: 6, color: theme.layer[2] }
            },
            width: {
              size: 625,
              type: docx.WidthType.DXA
            },
            shading: { fill: section.userCanScore ? getScorePropertiesForDOCX(section.data.score, theme).fill : '#FFFFFF' },
            verticalAlign: docx.VerticalAlign.CENTER
          })
        ]
      })
    ]
  });

  if (section.pdfLayout === 'TWO_COLUMN_GRID') {
    return [sectionHeaderTable, new docx.Paragraph({})];
  }

  const beforeSectionHeaderSpacing = new docx.Paragraph({
    spacing: { before: 100 }
  });

  return [beforeSectionHeaderSpacing, sectionHeaderTable, new docx.Paragraph({})];
};

export const formattingDueDiligenceRecordDataForDocx = async (
  dueDiligenceRecordTier: DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['funnel']['tiers'][number],
  investmentName: string,
  investmentDescription: string,
  templateStylingData: ITemplateStylingSettings,
  includeAiSuggested: boolean,
  theme: DefaultTheme
) => {
  const totalScore = dueDiligenceRecordTier.sections.reduce((accumulator, currentValue) => accumulator + currentValue.data.score, 0);
  const displayScore = dueDiligenceRecordTier.sections.some((section) => section.userCanScore);

  const headerFontColor = templateStylingData?.headerFontStyle?.fontColor ? `#${templateStylingData?.headerFontStyle?.fontColor}` : theme.font.strong;
  const accentColor = templateStylingData?.accentColor || '#5A6771';

  let docxData = [];

  docxData.push(
    ...createInvestmentParagraphsForDOCX({
      investmentName,
      templateName: dueDiligenceRecordTier?.name || '',
      investmentDescription,
      headerFontColor,
      theme,
      score: totalScore,
      displayScore
    })
  );

  for await (const section of dueDiligenceRecordTier.sections) {
    const fields = convertDueDiligenceFieldToFieldEditor(section?.fields || [], null);

    if (!shouldDisplaySection(fields, includeAiSuggested)) continue;

    if (section.pdfLayout === 'TWO_COLUMN_GRID') {
      docxData.push(new docx.Paragraph({ pageBreakBefore: true }));
    }

    docxData.push(...createSectionHeader(section, headerFontColor, theme));

    let sectionData;
    switch (section.pdfLayout) {
      case 'THREE_COLUMN':
        sectionData = await getThreeColumnSection(fields, includeAiSuggested, theme);
        break;
      case 'TWO_COLUMN_GRID':
        sectionData = await getTwoColumnSection(fields, includeAiSuggested, theme, headerFontColor, accentColor);
        break;
      default:
        sectionData = await getOneColumnSection(fields, includeAiSuggested, theme, headerFontColor, accentColor);
        break;
    }
    docxData.push(...sectionData);
  }

  return docxData;
};
